// ============================================================================
// HOOK - useVisualCircuit.js
// ============================================================================
// ./hooks/useVisualCircuit.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import visualCircuitAPI from '../api/visualCircuitAPI';

const useVisualCircuit = () => {
  const { 
    visualCircuitState,
    visualCircuitDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // CIRCUIT MANAGEMENT
  // ============================================================================

  const getAllCircuits = useCallback(async (params = {}) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getAllCircuits(params);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_CIRCUITS', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuits' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitBySessionId = useCallback(async (sessionId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitBySessionId(sessionId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_CURRENT_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitById = useCallback(async (id) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitById(id);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_CURRENT_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitByCircuitId = useCallback(async (circuitId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitByCircuitId(circuitId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_CURRENT_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const createCircuit = useCallback(async (data) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.createCircuit(data);
      if (response.success) {
        visualCircuitDispatch({ type: 'ADD_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to create circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const updateCircuit = useCallback(async (id, data) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.updateCircuit(id, data);
      if (response.success) {
        visualCircuitDispatch({ type: 'UPDATE_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to update circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const deleteCircuit = useCallback(async (id) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.deleteCircuit(id);
      if (response.success) {
        visualCircuitDispatch({ type: 'DELETE_CIRCUIT', payload: id });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to delete circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // CIRCUIT BY FILTERS
  // ============================================================================

  const getCircuitsByStudent = useCallback(async (studentId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitsByStudent(studentId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_STUDENT_CIRCUITS', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuits' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitsByLab = useCallback(async (labId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitsByLab(labId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_LAB_CIRCUITS', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuits' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitsByCourse = useCallback(async (courseId, studentId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitsByCourse(courseId, studentId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_COURSE_CIRCUITS', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuits' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getCircuitBySimulator = useCallback(async (sessionId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getCircuitBySimulator(sessionId);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_CURRENT_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // TEMPLATES
  // ============================================================================

  const getTemplates = useCallback(async (category = null) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getTemplates(category);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_TEMPLATES', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get templates' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // COMPONENT OPERATIONS
  // ============================================================================

  const addComponent = useCallback(async (id, component) => {
    try {
      const response = await visualCircuitAPI.addComponent(id, component);
      if (response.success) {
        visualCircuitDispatch({ type: 'ADD_COMPONENT', payload: { circuitId: id, component: response.data } });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to add component' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const removeComponent = useCallback(async (id, instanceId) => {
    try {
      const response = await visualCircuitAPI.removeComponent(id, instanceId);
      if (response.success) {
        visualCircuitDispatch({ type: 'REMOVE_COMPONENT', payload: { circuitId: id, instanceId } });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to remove component' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // CONNECTION OPERATIONS
  // ============================================================================

  const addConnection = useCallback(async (id, connection) => {
    try {
      const response = await visualCircuitAPI.addConnection(id, connection);
      if (response.success) {
        visualCircuitDispatch({ type: 'ADD_CONNECTION', payload: { circuitId: id, connection: response.data } });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to add connection' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const removeConnection = useCallback(async (id, connectionId) => {
    try {
      const response = await visualCircuitAPI.removeConnection(id, connectionId);
      if (response.success) {
        visualCircuitDispatch({ type: 'REMOVE_CONNECTION', payload: { circuitId: id, connectionId } });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to remove connection' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // VALIDATION & SYNC
  // ============================================================================

  const validateCircuit = useCallback(async (id) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.validateCircuit(id);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_VALIDATION_RESULT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to validate circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      visualCircuitDispatch({ type: 'SET_LOADING', payload: false }); // ✅ Always reset loading
    }
  }, [visualCircuitDispatch]);

  const syncCircuitToSimulator = useCallback(async (id) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.syncCircuitToSimulator(id);
      
      if (response.success) {
        visualCircuitDispatch({ type: 'SYNC_SUCCESS', payload: { circuitId: id, message: response.message } });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to sync circuit' });
      }
      
      return response;  // This already has { success, message, data }
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    } finally {
      visualCircuitDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // CLONE & STATISTICS
  // ============================================================================

  const cloneCircuit = useCallback(async (id, newStudentId) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.cloneCircuit(id, newStudentId);
      if (response.success) {
        visualCircuitDispatch({ type: 'ADD_CIRCUIT', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to clone circuit' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  const getStatistics = useCallback(async (params = {}) => {
    visualCircuitDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await visualCircuitAPI.getStatistics(params);
      if (response.success) {
        visualCircuitDispatch({ type: 'SET_STATISTICS', payload: response.data });
      } else {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to get statistics' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearError = useCallback(() => {
    visualCircuitDispatch({ type: 'CLEAR_ERROR' });
  }, [visualCircuitDispatch]);

  const clearCurrentCircuit = useCallback(() => {
    visualCircuitDispatch({ type: 'CLEAR_CURRENT_CIRCUIT' });
  }, [visualCircuitDispatch]);

  const clearValidationResult = useCallback(() => {
    visualCircuitDispatch({ type: 'CLEAR_VALIDATION_RESULT' });
  }, [visualCircuitDispatch]);

  const clearData = useCallback(() => {
    visualCircuitDispatch({ type: 'CLEAR_CIRCUIT_DATA' });
  }, [visualCircuitDispatch]);

  const updateNotebookOrder = useCallback(async (circuitId, orderUpdates) => {
    try {
      const response = await visualCircuitAPI.updateNotebookOrder(circuitId, orderUpdates);
      if (!response.success) {
        visualCircuitDispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to update notebook order' });
      }
      return response;
    } catch (error) {
      visualCircuitDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [visualCircuitDispatch]);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    circuits: visualCircuitState.circuits,
    currentCircuit: visualCircuitState.currentCircuit,
    studentCircuits: visualCircuitState.studentCircuits,
    labCircuits: visualCircuitState.labCircuits,
    courseCircuits: visualCircuitState.courseCircuits,
    templates: visualCircuitState.templates,
    statistics: visualCircuitState.statistics,
    validationResult: visualCircuitState.validationResult,
    isLoading: visualCircuitState.isLoading,
    error: visualCircuitState.error,

    // Circuit Management
    getAllCircuits,
    getCircuitBySessionId,
    getCircuitById,
    getCircuitByCircuitId,
    createCircuit,
    updateCircuit,
    deleteCircuit,

    // Circuit by Filters
    getCircuitsByStudent,
    getCircuitsByLab,
    getCircuitsByCourse,
    getCircuitBySimulator,

    // Templates
    getTemplates,

    // Component Operations
    addComponent,
    removeComponent,

    // Connection Operations
    addConnection,
    removeConnection,

    // Validation & Sync
    validateCircuit,
    syncCircuitToSimulator,

    // Clone & Statistics
    cloneCircuit,
    getStatistics,

    // Utility
    clearError,
    clearCurrentCircuit,
    clearValidationResult,
    clearData,
    updateNotebookOrder
  };
};

export default useVisualCircuit;